import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, FileDown } from "lucide-react";
import Logo from "@/components/Logo";
import PromoBanner from "@/components/PromoBanner";
import { useBrochureGate } from "@/components/BrochureGate";

const NAV = [
  { href: "/#products", label: "Products" },
  { href: "/#manufacturing", label: "Manufacturing" },
  { href: "/#why", label: "Why Saans" },
  { href: "/blog", label: "Blog" },
  { href: "/#faq", label: "FAQ" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { openGate } = useBrochureGate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 ${scrolled ? "bg-[#f9f8f5]/90 backdrop-blur-md border-b border-[#e7e0d6]" : "bg-transparent"}`}
      data-testid="navbar"
    >
      <PromoBanner />
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 md:px-8">
        <a href="/" data-testid="nav-logo" className="flex items-center">
          <Logo variant="horizontal" />
        </a>

        <nav className="hidden items-center gap-8 lg:flex">
          {NAV.map((n) => (
            <a key={n.href} href={n.href} data-testid={`nav-link-${n.label.toLowerCase().replace(/\s/g,"-")}`}
               className="text-sm font-medium text-[#4a4a48] transition-colors hover:text-[#d46a1d]">
              {n.label}
            </a>
          ))}
        </nav>

        <div className="hidden items-center gap-3 lg:flex">
          <button onClick={() => openGate("brochure")} data-testid="nav-brochure"
             className="inline-flex items-center gap-1.5 rounded-full border border-[#d8cfc1] bg-white px-4 py-2.5 text-sm font-semibold text-[#202124] transition-colors hover:border-[#d46a1d] hover:text-[#d46a1d]">
            <FileDown size={15} /> Brochure
          </button>
          <a href="#franchise" data-testid="nav-cta"
             className="rounded-full bg-[#d46a1d] px-5 py-2.5 text-sm font-semibold text-white transition-transform hover:scale-[1.03] hover:bg-[#c05f16]">
            Apply for Franchise
          </a>
        </div>

        <button onClick={() => setOpen(!open)} className="text-[#202124] lg:hidden" data-testid="nav-mobile-toggle" aria-label="Menu">
          {open ? <X size={24} /> : <Menu size={24} />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-[#e7e0d6] bg-[#f9f8f5] lg:hidden"
          >
            <div className="flex flex-col gap-1 p-5">
              {NAV.map((n) => (
                <a key={n.href} href={n.href} onClick={() => setOpen(false)} data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s/g,"-")}`}
                   className="rounded-lg px-3 py-3 text-base font-medium text-[#202124] hover:bg-[#f2ece4]">
                  {n.label}
                </a>
              ))}
              <button onClick={() => { setOpen(false); openGate("brochure"); }} data-testid="mobile-nav-brochure"
                 className="mt-2 inline-flex items-center justify-center gap-2 rounded-full border border-[#d8cfc1] bg-white px-5 py-3 text-center text-sm font-semibold text-[#202124]">
                <FileDown size={15} /> Download Brochure
              </button>
              <a href="#franchise" onClick={() => setOpen(false)}
                 className="mt-2 rounded-full bg-[#d46a1d] px-5 py-3 text-center text-sm font-semibold text-white">
                Apply for Franchise
              </a>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}
